"use client";

import { useState } from "react";
import Link from "next/link";
import { useUser } from "@clerk/nextjs";
import { useQuery } from "convex/react";

import { api } from "@soonlist/backend/convex/_generated/api";
import { deriveEventCta, linkBrandName } from "@soonlist/validators";

import { Bug, Check, ExternalLink, Ticket } from "~/components/icons";
import { cn, formatUrlForDisplay } from "~/lib/utils";
import { EventEnrichmentLinkIcon } from "./EventEnrichmentLinkIcon";
import { ReportEnrichmentDialog } from "./ReportEnrichmentDialog";

type EnrichmentLinkType =
  | "instagram"
  | "bandcamp"
  | "soundcloud"
  | "substack"
  | "ticketing"
  | "website";

const LINK_TYPE_LABELS: Record<EnrichmentLinkType, string> = {
  instagram: "Instagram",
  bandcamp: "Bandcamp",
  soundcloud: "SoundCloud",
  substack: "Substack",
  ticketing: "Tickets",
  website: "Website",
};

function formatHostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./i, "");
  } catch {
    return formatUrlForDisplay(url) || url;
  }
}

function typedLinkLabel(type: string): string {
  return LINK_TYPE_LABELS[type as EnrichmentLinkType] ?? "Link";
}

interface EntityLink {
  url: string;
  type: string;
  lowTrust?: boolean;
}

interface Entity {
  name: string;
  role?: string;
  confidence?: string;
  note?: string;
  links: EntityLink[];
}

interface Source {
  url: string;
  title?: string;
  snippet?: string;
}

export function EventEnrichmentSection({ eventId }: { eventId: string }) {
  const enrichment = useQuery(api.events.getEnrichment, { eventId });
  const { isSignedIn } = useUser();
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);

  if (enrichment?.status !== "ready") {
    return null;
  }

  const description = enrichment.description;
  const ticketUrl = enrichment.ticketUrl;
  const entities: Entity[] = enrichment.entities ?? [];
  const sources: Source[] = enrichment.sources ?? [];
  const vibe = enrichment.vibe;
  const goodToKnow = enrichment.goodToKnow;
  const hasGoodToKnow = !!goodToKnow?.trim();

  const hasContent =
    !!description ||
    !!ticketUrl ||
    !!enrichment.price ||
    entities.length > 0 ||
    !!vibe ||
    hasGoodToKnow ||
    sources.length > 0;
  if (!hasContent) {
    return null;
  }

  return (
    <section className="flex flex-col gap-5 rounded-3xl bg-white p-5 shadow-sm sm:p-6">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-neutral-1">
            More about this event
          </h2>
          <span className="rounded-full bg-accent-yellow px-2 py-0.5 text-xs font-medium text-neutral-1">
            Beta
          </span>
        </div>
        {isSignedIn && (
          <button
            type="button"
            onClick={() => setIsReportDialogOpen(true)}
            className="inline-flex shrink-0 items-center gap-1 text-xs text-neutral-2 transition-colors hover:text-neutral-1 hover:underline"
          >
            <Bug className="size-3" />
            Report incorrect info
          </button>
        )}
      </div>

      {isSignedIn && (
        <ReportEnrichmentDialog
          eventId={eventId}
          open={isReportDialogOpen}
          onOpenChange={setIsReportDialogOpen}
        />
      )}

      {description && (
        <p className="whitespace-pre-line text-base leading-relaxed text-neutral-1">
          {description}
        </p>
      )}

      {(() => {
        const cta = deriveEventCta({ price: enrichment.price, ticketUrl });
        if (cta.treatment === "none") return null;

        if (cta.treatment === "ticket" || cta.treatment === "info") {
          if (!cta.url) return null;
          const isPrimary = cta.treatment === "ticket";
          return (
            <div>
              <Link
                href={cta.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={cta.accessibilityLabel}
                className={cn(
                  "inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors",
                  isPrimary
                    ? "bg-interactive-1 text-white hover:bg-interactive-2"
                    : "bg-interactive-3 text-interactive-1 hover:bg-interactive-2",
                )}
              >
                {isPrimary ? (
                  <Ticket className="size-4" />
                ) : (
                  <ExternalLink className="size-4" />
                )}
                {cta.label}
              </Link>
            </div>
          );
        }

        const isGo = cta.treatment === "go";
        return (
          <div>
            <span
              className={cn(
                "inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold",
                isGo
                  ? "border border-green-200 bg-green-50 text-green-700"
                  : "bg-neutral-100 text-neutral-1",
              )}
            >
              {isGo && <Check className="size-4" />}
              {cta.label}
            </span>
          </div>
        );
      })()}

      {entities.length > 0 && (
        <div className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-2">
            Featured
          </h3>
          <ul className="flex flex-col gap-3">
            {entities.map((entity, entityIndex) => (
              <li
                key={`${entity.name}-${entityIndex}`}
                className="flex flex-col gap-1.5"
              >
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-medium text-neutral-1">
                    {entity.name}
                  </span>
                  {entity.role && (
                    <span className="text-sm text-neutral-2">
                      {entity.role}
                    </span>
                  )}
                </div>
                {entity.note && (
                  <p className="text-sm leading-relaxed text-neutral-2">
                    {entity.note}
                  </p>
                )}
                {entity.links.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {entity.links.map((link, linkIndex) => (
                      <Link
                        key={`${link.url}-${linkIndex}`}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={link.url}
                        className={cn(
                          "inline-flex items-center gap-1 rounded-full border border-neutral-3 px-2.5 py-1 text-xs transition-colors hover:border-interactive-1 hover:text-interactive-1",
                          link.lowTrust ? "text-neutral-3" : "text-neutral-2",
                        )}
                      >
                        <EventEnrichmentLinkIcon
                          type={link.type}
                          url={link.url}
                          size={12}
                        />
                        {linkBrandName(link.type, link.url) ??
                          typedLinkLabel(link.type)}
                      </Link>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {vibe && (
        <div className="flex flex-col gap-1.5">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-2">
            What to expect
          </h3>
          <p className="text-base leading-relaxed text-neutral-1">{vibe}</p>
        </div>
      )}

      {hasGoodToKnow && (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-2">
            Good to know
          </h3>
          <p className="text-base text-neutral-1">{goodToKnow}</p>
        </div>
      )}

      {sources.length > 0 && (
        <details className="group border-t border-neutral-3 pt-3">
          <summary className="flex cursor-pointer list-none items-center gap-1 text-sm font-medium text-neutral-2 hover:text-neutral-1">
            <ExternalLink className="size-3.5" />
            Sources ({sources.length})
          </summary>
          <ul className="mt-3 flex flex-col gap-3">
            {sources.map((source, index) => (
              <li
                key={`${source.url}-${index}`}
                className="flex flex-col gap-0.5"
              >
                <Link
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="break-all text-sm text-interactive-1 hover:underline"
                >
                  {source.title || formatHostname(source.url)}
                </Link>
                {source.snippet && (
                  <span className="text-xs text-neutral-2">
                    {source.snippet}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
